/**
 * What an operator READS for a product code. Never the code itself.
 *
 * The Create Tenant summary printed `chatbot × 5`, `live_chat × 5` and
 * `ai_features × 1`, and the Subscription tab printed `item_code` in its
 * Product column. Those are identifiers: the key a `tenant_app_assignments`
 * row carries, not the name of anything a customer bought.
 *
 * THIS IS NOT A CATALOGUE. The names come from the server — `labels` on
 * `GET /commercial/presentation` and on `/tenants/{id}/commercial-summary`,
 * both built from the canonical registry by `product_labels()`. This file
 * only formats them. A code the server did not name is title-cased rather
 * than dropped, so an unknown product still shows up instead of vanishing.
 */

// `ai_calling` moved here 26 Sep 2026 when main made `humanise` the single
// derivation for channel labels. The title-case fallback renders it "Ai
// Calling" — a lowercase i, beside a product row whose own label reads "AI
// Calling". Keeping it in this map rather than in one caller means every
// surface that humanises a code gets the right spelling, which is the whole
// reason the map is here.
const ACRONYMS = { sms: 'SMS', ai_calling: 'AI Calling' };

/** Title-case an identifier. The last resort, never the first. */
export const humanise = (code) => {
  if (ACRONYMS[code]) return ACRONYMS[code];
  return String(code ?? '')
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

export const productLabel = (code, labels) =>
  (labels && labels[code]) || humanise(code);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * One entitlement from `validate()`, as a sentence fragment.
 *
 *   Assistants × 5 users
 *   Live Chat (included with Assistants) × 5 agents
 *   Cluvik AI — whole workspace
 *
 * `perTenant` is the set of codes the presentation marks
 * `configuration_kind: 'tenant'`: priced once for the workspace, so a count
 * of 1 is not a number of people and must not be printed as one.
 *
 * A GRANTED entitlement's seats are the granting product's seats — the
 * Assistants users ARE the people who answer Live Chat — so they are agents.
 */
export function entitlementText(e, labels, perTenant = new Set()) {
  const name = productLabel(e.app_code, labels);
  const caps = (e.capabilities || []).map((c) => productLabel(c, labels));
  const tail = caps.length ? ` · ${caps.join(', ')}` : '';
  const seats = Number(e.seats) || 0;
  if (e.source === 'granted' && e.granted_by) {
    return `${name} (included with ${productLabel(e.granted_by, labels)}) × `
      + `${plural(seats, 'agent', 'agents')}${tail}`;
  }
  if (perTenant.has(e.app_code)) return `${name} — whole workspace${tail}`;
  return `${name} × ${plural(seats, 'user', 'users')}${tail}`;
}

/** Operator-readable text for a server problem. The server's `detail` is
 *  prose already; the fallback used to be `${field}: ${code}`, which put
 *  `lines.chatbot: not_sellable` on screen. */
export const problemText = (p) =>
  p?.detail || 'The server refused this configuration.';
